import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Edit3, Trash2, Check } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ax = axios.create({ withCredentials: true });

// Pull a human-readable message out of an axios error response. FastAPI
// returns `detail` as either a plain string (custom HTTPException) or an
// array of validator objects (`[{loc, msg, type}, ...]` for 422). If we
// pass the array straight into toast.error() it blows up React with
// "Objects are not valid as a React child" — which is what was blanking
// the whole Admin dashboard on juror-create validation failures.
const fmtApiError = (e, fallback = 'Request failed.') => {
  const d = e?.response?.data?.detail;
  if (typeof d === 'string') return d;
  if (Array.isArray(d)) {
    return d.map((x) => (x && typeof x === 'object' ? (x.msg || JSON.stringify(x)) : String(x))).join('; ') || fallback;
  }
  if (d && typeof d === 'object') return d.msg || JSON.stringify(d);
  return fallback;
};

const BLANK_JUROR = { name: '', email: '', password: '', title: '', company: '', assigned_categories: [], active: true };

const AdminJurorsTab = () => {
  const [jurors, setJurors] = useState([]);
  const [allCats, setAllCats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | 'new' | juror object
  const [draft, setDraft] = useState(BLANK_JUROR);
  const [saving, setSaving] = useState(false);
  const [deleteId, setDeleteId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [j, c] = await Promise.all([
        ax.get(`${API}/admin/jurors`),
        ax.get(`${API}/admin/categories-list`),
      ]);
      setJurors(j.data);
      setAllCats(c.data);
    } catch (e) {
      toast.error(fmtApiError(e, 'Failed to load jurors.'));
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openNew = () => { setDraft({ ...BLANK_JUROR }); setEditing('new'); };
  const openEdit = (j) => { setDraft({ ...BLANK_JUROR, ...j, password: '' }); setEditing(j); };

  const toggleCat = (c) => {
    setDraft((d) => {
      const has = d.assigned_categories.includes(c);
      return { ...d, assigned_categories: has ? d.assigned_categories.filter((x) => x !== c) : [...d.assigned_categories, c] };
    });
  };

  const save = async () => {
    if (!draft.name?.trim() || !draft.email?.trim()) {
      toast.error('Name and login ID are required.');
      return;
    }
    if (editing === 'new' && (!draft.password || draft.password.length < 8)) {
      toast.error('Password must be at least 8 characters.');
      return;
    }
    setSaving(true);
    try {
      if (editing === 'new') {
        await ax.post(`${API}/admin/jurors`, draft);
        toast.success('Juror created.');
      } else {
        const updates = { ...draft };
        if (!updates.password) delete updates.password;
        delete updates.email; // immutable
        delete updates.id;
        delete updates.assignments_count;
        delete updates.completed_count;
        delete updates.created_at;
        delete updates.password_hash;
        await ax.patch(`${API}/admin/jurors/${editing.id}`, updates);
        toast.success('Juror updated.');
      }
      setEditing(null);
      load();
    } catch (e) {
      toast.error(fmtApiError(e, 'Save failed.'));
    } finally { setSaving(false); }
  };

  const doDelete = async () => {
    if (!deleteId) return;
    setSaving(true);
    try {
      await ax.delete(`${API}/admin/jurors/${deleteId}`);
      toast.success('Juror removed.');
      setDeleteId(null);
      load();
    } catch (e) {
      toast.error(fmtApiError(e, 'Delete failed.'));
    } finally { setSaving(false); }
  };

  return (
    <div data-testid="admin-jurors-tab">
      <div className="flex items-center justify-between mb-5">
        <p className="text-[var(--ink-muted)] text-sm">Create juror accounts, assign categories, and manage access.</p>
        <button onClick={openNew} data-testid="jurors-new-btn" className="inline-flex items-center gap-2 bg-[var(--gold)] hover:bg-[var(--gold-bright)] text-[var(--bg)] text-[10px] tracking-[0.3em] uppercase px-5 py-3 transition-colors">
          <Plus size={12} /> Add Juror
        </button>
      </div>

      {loading ? (
        <div className="py-16 text-center text-[var(--ink-muted)] tracking-[0.3em] uppercase text-xs">Loading…</div>
      ) : jurors.length === 0 ? (
        <div className="py-16 border border-dashed border-[var(--gold-faint)] text-center text-[var(--ink-muted)]" data-testid="jurors-empty">No jurors yet. Add one to get started.</div>
      ) : (
        <div className="overflow-x-auto border border-[var(--gold-faint)]">
          <table className="w-full text-sm" data-testid="jurors-table">
            <thead className="bg-[var(--bg-deep)]/50">
              <tr>
                {['Name', 'Login ID', 'Title', 'Company', 'Categories', 'Progress', 'Status', 'Actions'].map((h) => (
                  <th key={h} className="text-left px-4 py-3 text-[10px] tracking-[0.25em] uppercase text-[var(--ink-muted)] font-normal">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {jurors.map((j, i) => (
                <tr key={j.id} className="border-t border-[var(--gold-faint)]/40 hover:bg-[var(--gold-faint)]/10" data-testid={`juror-row-${i}`}>
                  <td className="px-4 py-3 text-[var(--ink)]">{j.name}</td>
                  <td className="px-4 py-3 text-[var(--ink-muted)]">{j.email}</td>
                  <td className="px-4 py-3 text-[var(--ink-muted)]">{j.title || '—'}</td>
                  <td className="px-4 py-3 text-[var(--ink-muted)]">{j.company || '—'}</td>
                  <td className="px-4 py-3 text-[var(--gold)] text-xs">{j.assigned_categories?.length || 0}</td>
                  <td className="px-4 py-3 text-[var(--ink-muted)] text-xs">{j.completed_count || 0} / {j.assignments_count || 0}</td>
                  <td className="px-4 py-3 text-xs">
                    {j.active === false ? <span className="text-[var(--terracotta)]">Inactive</span> : <span className="text-[#7BC47F]">Active</span>}
                  </td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    <button onClick={() => openEdit(j)} data-testid={`juror-edit-${i}`} className="text-[var(--ink)] hover:text-[var(--gold)] text-xs tracking-[0.2em] uppercase mr-3">Edit</button>
                    <button onClick={() => setDeleteId(j.id)} data-testid={`juror-delete-${i}`} className="text-[var(--terracotta)] text-xs tracking-[0.2em] uppercase">Delete</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Create/Edit modal */}
      {editing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="juror-modal">
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => setEditing(null)} />
          <div className="relative bg-[var(--bg-alt)] border border-[var(--gold-faint)] max-w-3xl w-full max-h-[88vh] overflow-y-auto">
            <div className="sticky top-0 bg-[var(--bg-alt)] border-b border-[var(--gold-faint)] px-8 py-5 flex items-center justify-between z-10">
              <h2 className="font-[Libre_Baskerville] text-2xl text-[var(--ink)]">{editing === 'new' ? 'Add Juror' : 'Edit Juror'}</h2>
              <button onClick={() => setEditing(null)} className="text-[var(--ink-muted)] hover:text-[var(--gold)] text-2xl leading-none">×</button>
            </div>
            <div className="p-8 space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <TextField label="Full Name *" value={draft.name} onChange={(v) => setDraft((d) => ({ ...d, name: v }))} tid="juror-edit-name" />
                <TextField label="Login ID *" type="text" value={draft.email} onChange={(v) => setDraft((d) => ({ ...d, email: v }))} disabled={editing !== 'new'} tid="juror-edit-email" hint="Any unique identifier (e.g. jury_user_01) or an email address. No spaces." />
                <TextField label="Title (CMO, Founder…)" value={draft.title} onChange={(v) => setDraft((d) => ({ ...d, title: v }))} tid="juror-edit-title" />
                <TextField label="Company" value={draft.company} onChange={(v) => setDraft((d) => ({ ...d, company: v }))} tid="juror-edit-company" />
                <TextField label={editing === 'new' ? 'Password (min 8) *' : 'Reset Password (leave blank to keep)'} type="password" value={draft.password} onChange={(v) => setDraft((d) => ({ ...d, password: v }))} tid="juror-edit-password" />
                {editing !== 'new' && (
                  <div>
                    <label className="block text-[10px] tracking-[0.3em] uppercase text-[var(--ink-muted)] mb-2">Status</label>
                    <button type="button" onClick={() => setDraft((d) => ({ ...d, active: !d.active }))} data-testid="juror-edit-active" className={`w-full border px-3 py-2 text-sm transition-colors ${draft.active ? 'border-[#7BC47F] text-[#7BC47F]' : 'border-[var(--terracotta)] text-[var(--terracotta)]'}`}>
                      {draft.active ? 'Active' : 'Inactive'} — click to toggle
                    </button>
                  </div>
                )}
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-[10px] tracking-[0.3em] uppercase text-[var(--ink-muted)]">Assigned Categories ({draft.assigned_categories.length})</label>
                  {allCats.length > 0 && (
                    <button type="button" onClick={() => setDraft((d) => ({ ...d, assigned_categories: d.assigned_categories.length === allCats.length ? [] : [...allCats] }))} className="text-[10px] tracking-[0.3em] uppercase text-[var(--gold)] hover:text-[var(--gold-bright)]">
                      {draft.assigned_categories.length === allCats.length ? 'Clear All' : 'Select All'}
                    </button>
                  )}
                </div>
                {allCats.length === 0 ? (
                  <p className="text-[var(--ink-muted)] text-xs italic">No categories with submissions yet. The juror can be assigned later.</p>
                ) : (
                  <div className="border border-[var(--gold-faint)] max-h-64 overflow-y-auto" data-testid="juror-edit-categories">
                    {allCats.map((c) => {
                      const checked = draft.assigned_categories.includes(c);
                      return (
                        <label key={c} className={`flex items-center gap-3 px-4 py-2.5 border-b border-[var(--gold-faint)]/30 cursor-pointer ${checked ? 'bg-[var(--gold)]/10' : 'hover:bg-[var(--gold)]/5'}`}>
                          <input type="checkbox" checked={checked} onChange={() => toggleCat(c)} className="sr-only" />
                          <span className={`w-4 h-4 border flex items-center justify-center ${checked ? 'bg-[var(--gold)] border-[var(--gold)]' : 'border-[var(--gold)]/40'}`}>
                            {checked && <Check size={11} className="text-black" strokeWidth={3} />}
                          </span>
                          <span className="text-[var(--ink)] text-[13px]">{c}</span>
                        </label>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
            <div className="sticky bottom-0 bg-[var(--bg-alt)] border-t border-[var(--gold-faint)] px-8 py-4 flex justify-end gap-3">
              <button onClick={() => setEditing(null)} className="text-[var(--ink-muted)] text-xs tracking-[0.2em] uppercase px-4 py-2">Cancel</button>
              <button onClick={save} disabled={saving} data-testid="juror-edit-save" className="bg-[var(--gold)] hover:bg-[var(--gold-bright)] text-[var(--bg)] text-xs tracking-[0.2em] uppercase px-6 py-2 disabled:opacity-50">
                {saving ? 'Saving…' : editing === 'new' ? 'Create Juror' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete confirm */}
      {deleteId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="juror-delete-modal">
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => setDeleteId(null)} />
          <div className="relative bg-[var(--bg-alt)] border border-[var(--terracotta-soft)] max-w-md w-full p-8">
            <h3 className="font-[Libre_Baskerville] text-xl text-[var(--ink)] mb-2">Remove juror?</h3>
            <p className="text-[var(--ink-muted)] text-sm">This will revoke their access and delete all of their evaluations. This cannot be undone.</p>
            <div className="flex justify-end gap-3 mt-6">
              <button onClick={() => setDeleteId(null)} className="text-[var(--ink-muted)] text-xs tracking-[0.2em] uppercase px-4 py-2">Cancel</button>
              <button onClick={doDelete} disabled={saving} data-testid="juror-delete-confirm" className="bg-[var(--terracotta)] hover:opacity-90 text-white text-xs tracking-[0.2em] uppercase px-6 py-2 disabled:opacity-50">
                {saving ? 'Removing…' : 'Remove permanently'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const TextField = ({ label, value, onChange, type = 'text', tid, disabled, hint }) => (
  <div>
    <label className="block text-[10px] tracking-[0.3em] uppercase text-[var(--ink-muted)] mb-2">{label}</label>
    <input data-testid={tid} type={type} value={value ?? ''} disabled={disabled} onChange={(e) => onChange(e.target.value)} className="w-full bg-[var(--bg)] border border-[var(--gold-faint)] focus:border-[var(--gold)] outline-none px-3 py-2 text-[var(--ink)] text-sm disabled:opacity-60" />
    {hint && <p className="text-[var(--ink-muted)] text-[11px] mt-1.5 italic leading-snug">{hint}</p>}
  </div>
);

export default AdminJurorsTab;
